import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, MessageSquarePlus, StickyNote, History, ShieldCheck, BadgeCheck, LogIn, Globe2 } from "lucide-react";

const REGION_OPTIONS = [
  { v: "all", label: "All regions", flag: "🌍" },
  { v: "in",  label: "India",        flag: "🇮🇳" },
  { v: "us",  label: "USA",          flag: "🇺🇸" },
  { v: "row", label: "Rest of world", flag: "🌐" },
];

const REGION_META = {
  in:  { flag: "🇮🇳", label: "India",       color: "#FF9933" },
  us:  { flag: "🇺🇸", label: "USA",         color: "#3C3B6E" },
  row: { flag: "🌐",  label: "Rest of world", color: "#7A7A7A" },
};

const STATUS_OPTIONS = [
  { v: "all", label: "All" },
  { v: "active", label: "Active" },
  { v: "pending_review", label: "Pending review" },
  { v: "suspended", label: "Suspended" },
  { v: "blocked", label: "Blocked" },
  { v: "deleted", label: "Deleted" },
];

const STATUS_COLORS = {
  active: "#5C6B52",
  pending_review: "#B25D38",
  suspended: "#D2691E",
  blocked: "#B23A3A",
  deleted: "#7A7A7A",
};

// Compact badges showing which sign-in providers a user has connected.
// Data source: `user.firebase_uids` (map of provider → uid) plus
// `user.login_history[].provider` for legacy JWT accounts that never
// completed a Firebase sign-in.
const PROVIDER_META = {
  google:            { letter: "G", label: "Google",        color: "#DB4437" },
  "phone.firebase":  { letter: "P", label: "Phone (SMS)",   color: "#0F9D58" },
  phone:             { letter: "P", label: "Phone (legacy)", color: "#0F9D58" },
  email:             { letter: "E", label: "Email link",    color: "#4285F4" },
  password:          { letter: "L", label: "Legacy password", color: "#7A7A7A" },
  device_trust:      { letter: "D", label: "Device trust",  color: "#8B5CF6" },
};

function ProviderBadges({ user }) {
  const providers = new Set();
  Object.keys(user?.firebase_uids || {}).forEach((k) => providers.add(k));
  (user?.login_history || []).forEach((h) => h.provider && providers.add(h.provider));
  if (!providers.size && user?.password_hash) providers.add("password");
  if (!providers.size) return <span className="text-[10px] opacity-50">—</span>;
  return (
    <div className="flex gap-1">
      {[...providers].map((p) => {
        const meta = PROVIDER_META[p] || { letter: "?", label: p, color: "#666" };
        return (
          <span
            key={p}
            title={meta.label}
            className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-mono font-bold text-white"
            style={{ background: meta.color }}
          >
            {meta.letter}
          </span>
        );
      })}
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return d.toLocaleDateString();
}

function StatusBadge({ status }) {
  const s = status || "active";
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider text-white"
      style={{ background: STATUS_COLORS[s] || "#666" }}
    >
      {s.replace("_", " ")}
    </span>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const isSuperAdmin = (me?.platform_role || (me?.is_admin ? "admin" : "user")) === "super_admin";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");
  const [region, setRegion] = useState("all");
  const [regionStats, setRegionStats] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // active user drawer
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/users", {
        params: { status, region, search: search || undefined, limit: 200 },
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const loadRegionStats = async () => {
    try {
      const { data } = await http.get("/admin/users/region-stats");
      setRegionStats(data);
    } catch { /* silent — non-critical */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, region]);
  useEffect(() => { loadRegionStats(); }, []);

  const openUser = async (u) => {
    setSelected(u);
    try {
      const { data } = await http.get("/admin/audit-events", {
        params: { entity_type: "user", entity_id: u.id, limit: 200 },
      });
      setHistory(data.items);
    } catch { setHistory([]); }
  };

  const doStatusChange = async (u, newStatus, comment) => {
    try {
      const { data } = await http.patch(`/admin/users/${u.id}/status`, { status: newStatus, comment });
      toast.success(`Status set to ${newStatus}`);
      setItems((xs) => xs.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
      if (selected?.id === u.id) openUser(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const sendWarning = async (u, message) => {
    if (!message?.trim()) return;
    try {
      await http.post(`/admin/users/${u.id}/warning`, { message });
      toast.success("Warning recorded");
      openUser(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const addNote = async (u, comment) => {
    if (!comment?.trim()) return;
    try {
      await http.post(`/admin/users/${u.id}/note`, { comment });
      toast.success("Note added");
      openUser(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const setRole = async (u, platform_role, organization_name) => {
    try {
      const { data } = await http.patch(`/admin/users/${u.id}/role`, { platform_role, organization_name });
      toast.success(`Role set to ${platform_role.replace("_", " ")}`);
      setItems((xs) => xs.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
      if (selected?.id === u.id) openUser(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Only Super Admins can change roles");
    }
  };

  const setTrusted = async (u, granted) => {
    try {
      const { data } = await http.patch(`/admin/users/${u.id}/trusted-publisher`, null, { params: { granted } });
      toast.success(granted ? "Trusted publisher granted (blue tick)" : "Trusted publisher revoked");
      setItems((xs) => xs.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
      if (selected?.id === u.id) openUser(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const exportCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/events?entity_type=user&format=csv`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="admin-users-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">USERS</div>
          <h2 className="font-display text-3xl">All registrations</h2>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{total} accounts total.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="admin-users-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name / email / phone"
            className="rounded-lg border px-4 py-2 bg-transparent text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
          />
          <button onClick={load} className="wofy-btn-ghost text-sm">Search</button>
          <button onClick={exportCSV} data-testid="admin-users-export" className="wofy-btn-ghost text-sm">Export CSV</button>
        </div>
      </div>

      {/* India KPI card + region filter chips */}
      {regionStats && (
        <div
          className="mt-6 rounded-xl border p-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          style={{ borderColor: "#FF993355", background: "linear-gradient(135deg, #FFF8EC 0%, #FFEED4 100%)" }}
          data-testid="admin-region-stats"
        >
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8A5C1A" }}>India users</div>
            <div className="font-display text-3xl" style={{ color: "#3B2E1F" }} data-testid="admin-region-india-count">
              {regionStats.india.total} <span className="text-sm opacity-60">/ {regionStats.total_users}</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8A5C1A" }}>DPDP consented</div>
            <div className="font-display text-3xl" style={{ color: "#3B2E1F" }}>
              {regionStats.india.dpdp_consented}
              {regionStats.india.dpdp_missing > 0 && (
                <span className="text-xs align-super ml-1" style={{ color: "#B23A3A" }}>·{regionStats.india.dpdp_missing} pending</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8A5C1A" }}>Phone-only signups</div>
            <div className="font-display text-3xl" style={{ color: "#3B2E1F" }}>{regionStats.india.phone_only}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8A5C1A" }}>Via IFT · SP</div>
            <div className="font-display text-2xl leading-tight" style={{ color: "#3B2E1F" }}>
              {regionStats.india.via_indianfamilytree}
              <span className="text-sm opacity-50 mx-1">·</span>
              {regionStats.india.via_socialpulse}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-users-region-filters">
        {REGION_OPTIONS.map((r) => (
          <button
            key={r.v}
            onClick={() => setRegion(r.v)}
            data-testid={`admin-users-region-${r.v}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors border ${region === r.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={region === r.v
              ? { background: "#FF9933", color: "#1A0F00", borderColor: "#FF9933" }
              : { borderColor: "var(--wofy-border)" }}
          >
            <span className="mr-1">{r.flag}</span>{r.label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.v}
            onClick={() => setStatus(s.v)}
            data-testid={`admin-users-filter-${s.v}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${status === s.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={status === s.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : {}}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        {loading ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No users match.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left font-mono text-[11px] uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>Joined</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>Name</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>Contact</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }} title="Sign-in providers: G=Google, P=Phone SMS, E=Email link">Providers</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }} title="Detected data region — India users are subject to DPDP consent rules">Region</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>Last seen</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>Role</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>Status</th>
                <th className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>Manage</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="hover:bg-[color:var(--wofy-surface)]/50">
                  <td className="py-3 px-3 border-b font-mono text-[11px]" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
                    {new Date(u.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>
                    <div className="font-medium">
                      {u.name}
                      {u.is_trusted_publisher && (
                        <BadgeCheck className="inline w-3.5 h-3.5 ml-1 -mt-0.5" style={{ color: "#1D4ED8" }} aria-label="Trusted publisher" />
                      )}
                    </div>
                    {u.is_admin && <span className="text-[10px] font-mono uppercase" style={{ color: "var(--wofy-primary)" }}>Admin</span>}
                  </td>
                  <td className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>
                    <div>{u.email || "—"}</div>
                    {u.phone && <div className="text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>{u.phone}{u.is_whatsapp ? " · WhatsApp" : ""}</div>}
                  </td>
                  <td className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-user-providers-${u.id}`}>
                    <ProviderBadges user={u} />
                  </td>
                  <td className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-user-region-${u.id}`}>
                    {(() => {
                      const rm = REGION_META[u.data_region] || REGION_META.row;
                      return (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                          style={{
                            background: rm.color + "22",
                            color: rm.color,
                            border: `1px solid ${rm.color}66`,
                          }}
                          title={u.dpdp_consent
                            ? `Consented DPDP on ${u.dpdp_consent_at?.slice(0,10) || "?"}`
                            : (u.data_region === "in" ? "India user — DPDP consent NOT yet recorded" : rm.label)}
                        >
                          <span>{rm.flag}</span>
                          <span className="uppercase tracking-wider text-[10px] font-mono">{u.data_region || "row"}</span>
                          {u.data_region === "in" && !u.dpdp_consent && (
                            <span className="ml-0.5" title="DPDP consent missing" style={{ color: "#B23A3A" }}>!</span>
                          )}
                          {u.data_region === "in" && u.nominee && (
                            <span className="ml-0.5 text-[10px]" title={`Nominee: ${u.nominee.name}${u.nominee.verified_at ? " (verified)" : ""}`} style={{ color: u.nominee.verified_at ? "#5C6B52" : "#B58936" }}>
                              {u.nominee.verified_at ? "✓" : "◔"}
                            </span>
                          )}
                          {u.data_region === "in" && !u.nominee && (
                            <span className="ml-0.5 text-[10px]" title="India user without nominee — DPDP compliance gap" style={{ color: "#B23A3A" }}>◌</span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-3 px-3 border-b font-mono text-[11px]" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }} data-testid={`admin-user-lastseen-${u.id}`}>
                    <span title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never signed in"}>
                      {relativeTime(u.last_login_at)}
                    </span>
                    {u.last_login_site && (
                      <div className="text-[10px] opacity-70">on {u.last_login_site}</div>
                    )}
                  </td>
                  <td className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>{u.role}</td>
                  <td className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="py-3 px-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>
                    <button
                      onClick={() => openUser(u)}
                      data-testid={`admin-user-manage-${u.id}`}
                      className="wofy-btn-ghost text-xs"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <UserDrawer
          user={selected}
          history={history}
          canManageRoles={isSuperAdmin}
          onClose={() => { setSelected(null); setHistory([]); }}
          onStatusChange={(s, c) => doStatusChange(selected, s, c)}
          onWarn={(m) => sendWarning(selected, m)}
          onNote={(m) => addNote(selected, m)}
          onRole={(role, orgName) => setRole(selected, role, orgName)}
          onTrusted={(granted) => setTrusted(selected, granted)}
        />
      )}
    </div>
  );
}

function UserDrawer({ user, history, onClose, onStatusChange, onWarn, onNote, onRole, canManageRoles, onTrusted }) {
  const [newStatus, setNewStatus] = useState(user.status || "active");
  const [statusComment, setStatusComment] = useState("");
  const [warning, setWarning] = useState("");
  const [note, setNote] = useState("");
  const [newRole, setNewRole] = useState(user.platform_role || (user.is_admin ? "admin" : "user"));
  const [orgName, setOrgName] = useState(user.organization_name || "");

  return (
    <div className="fixed inset-0 z-40 bg-[#14213D]/25 backdrop-blur-sm" onClick={onClose}>
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-[560px] overflow-y-auto p-8 shadow-2xl"
        style={{ background: "var(--wofy-paper)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-user-drawer"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">MANAGE USER</div>
            <h3 className="font-display text-2xl mt-1">{user.name}</h3>
            <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{user.email || user.phone}</p>
            <div className="mt-2"><StatusBadge status={user.status} /></div>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost text-xs">Close</button>
        </div>

        {/* Status change */}
        <div className="mt-8 wofy-card">
          <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Change status</div>
          <div className="mt-3 flex gap-2 flex-wrap">
            {STATUS_OPTIONS.filter((s) => s.v !== "all").map((s) => (
              <label key={s.v} className="cursor-pointer">
                <input
                  type="radio"
                  name="new-status"
                  value={s.v}
                  checked={newStatus === s.v}
                  onChange={() => setNewStatus(s.v)}
                  className="sr-only"
                />
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs ${newStatus === s.v ? "text-white" : ""}`}
                  style={newStatus === s.v ? { background: STATUS_COLORS[s.v] } : { border: "1px solid var(--wofy-border)" }}
                >
                  {s.label}
                </span>
              </label>
            ))}
          </div>
          <textarea
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            placeholder="Reason / comment (optional)"
            rows={2}
            data-testid="admin-user-status-comment"
            className="w-full mt-3 rounded-lg border px-3 py-2 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          />
          <button
            onClick={() => { onStatusChange(newStatus, statusComment); setStatusComment(""); }}
            data-testid="admin-user-status-save"
            className="wofy-btn-primary mt-3 text-sm"
          >
            Save status
          </button>
        </div>

        {/* Warning */}
        <div className="mt-6 wofy-card">
          <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
            <AlertTriangle className="w-3 h-3" /> Send warning
          </div>
          <textarea
            value={warning}
            onChange={(e) => setWarning(e.target.value)}
            placeholder="What is the user being warned about?"
            rows={2}
            data-testid="admin-user-warning-input"
            className="w-full mt-3 rounded-lg border px-3 py-2 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          />
          <button
            onClick={() => { onWarn(warning); setWarning(""); }}
            data-testid="admin-user-warning-send"
            className="wofy-btn-primary mt-3 text-sm"
            style={{ background: "#B23A3A" }}
          >
            <MessageSquarePlus className="w-4 h-4" /> Send warning
          </button>
        </div>

        {/* Note */}
        <div className="mt-6 wofy-card">
          <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
            <StickyNote className="w-3 h-3" /> Internal note
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note visible only to admins"
            rows={2}
            data-testid="admin-user-note-input"
            className="w-full mt-3 rounded-lg border px-3 py-2 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          />
          <button
            onClick={() => { onNote(note); setNote(""); }}
            data-testid="admin-user-note-save"
            className="wofy-btn-ghost mt-3 text-sm"
          >
            Save note
          </button>
        </div>

        {/* Trusted publisher badge */}
        <div className="mt-6 wofy-card" data-testid="admin-user-trusted-card">
          <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
            <BadgeCheck className="w-3 h-3" /> Trusted publisher badge
          </div>
          <p className="mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            When granted, this user's news submissions <b>skip admin review</b> and publish instantly. A blue verified tick appears next to their name across the site.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="text-sm flex items-center gap-1.5">
              Current:
              {user.is_trusted_publisher ? (
                <span className="font-semibold inline-flex items-center gap-1" style={{ color: "#1D4ED8" }}>
                  Trusted <BadgeCheck className="w-4 h-4" />
                </span>
              ) : (
                <span style={{ color: "var(--wofy-text-soft)" }}>Not trusted</span>
              )}
            </div>
            <div className="ml-auto flex gap-2">
              {user.is_trusted_publisher ? (
                <button
                  onClick={() => onTrusted(false)}
                  data-testid="admin-user-trusted-revoke"
                  className="wofy-btn-ghost text-sm"
                >
                  Revoke trusted
                </button>
              ) : (
                <button
                  onClick={() => onTrusted(true)}
                  data-testid="admin-user-trusted-grant"
                  className="wofy-btn-primary text-sm"
                  style={{ background: "#1D4ED8" }}
                >
                  <BadgeCheck className="w-4 h-4" /> Grant trusted
                </button>
              )}
            </div>
          </div>
          {user.trusted_publisher_at && (
            <p className="mt-2 text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
              {user.is_trusted_publisher ? "Granted" : "Last granted"} {new Date(user.trusted_publisher_at).toLocaleString()} by {user.trusted_publisher_by || "—"}
            </p>
          )}
        </div>

        {/* Role assignment — Super Admin only */}
        {canManageRoles && (
          <div className="mt-6 wofy-card" data-testid="admin-user-role-card">
            <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
              <ShieldCheck className="w-3 h-3" /> Platform role
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[
                { v: "user", label: "User" },
                { v: "editor", label: "Editor (content only)" },
                { v: "admin", label: "Admin / Moderator" },
                { v: "organization_admin", label: "Organization Admin" },
                { v: "super_admin", label: "Super Admin" },
              ].map((r) => (
                <label
                  key={r.v}
                  className={`cursor-pointer rounded-lg border p-3 text-sm font-medium text-center ${newRole === r.v ? "text-white" : ""}`}
                  style={newRole === r.v ? { background: "var(--wofy-primary)", borderColor: "transparent" } : { borderColor: "var(--wofy-border)" }}
                >
                  <input
                    data-testid={`admin-user-role-${r.v}`}
                    type="radio"
                    name={`role-${user.id}`}
                    value={r.v}
                    checked={newRole === r.v}
                    onChange={() => setNewRole(r.v)}
                    className="sr-only"
                  />
                  {r.label}
                </label>
              ))}
            </div>
            {newRole === "organization_admin" && (
              <label className="block mt-3">
                <span className="text-[11px] font-mono uppercase tracking-wider">Organization name</span>
                <input
                  data-testid="admin-user-org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. Feeding America"
                  className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent"
                  style={{ borderColor: "var(--wofy-border)" }}
                />
              </label>
            )}
            <button
              onClick={() => onRole(newRole, orgName)}
              data-testid="admin-user-role-save"
              className="wofy-btn-primary mt-3 text-sm"
            >
              Save role
            </button>
            <p className="mt-3 text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
              <b>Organization Admin</b> can instant-publish causes on behalf of a verified org.
              <b> Super Admin</b> has full platform control including instant-publish with the "Official Social Pulse Post" badge.
            </p>
          </div>
        )}

        {/* Sign-in activity — auth debugging visible at a glance:
            connected providers, per-provider Firebase UIDs, per-login
            channel (site/provider/timestamp/IP), and cross-site usage. */}
        <div className="mt-6 wofy-card" data-testid="admin-user-signin-card">
          <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
            <LogIn className="w-3 h-3" /> Sign-in activity
          </div>

          <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
            <div>
              <div className="opacity-60 mb-1">Total sign-ins</div>
              <div className="font-mono text-base">{(user.login_history || []).length}</div>
            </div>
            <div>
              <div className="opacity-60 mb-1">Last sign-in</div>
              <div className="font-mono">{relativeTime(user.last_login_at)}</div>
              {user.last_login_site && <div className="text-[10px] opacity-60">on {user.last_login_site}</div>}
            </div>
          </div>

          {/* Connected Firebase identities per provider */}
          {user.firebase_uids && Object.keys(user.firebase_uids).length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] opacity-60 uppercase font-mono mb-2">Connected identities</div>
              <div className="space-y-1">
                {Object.entries(user.firebase_uids).map(([provider, uid]) => {
                  const meta = PROVIDER_META[provider] || { label: provider, color: "#666" };
                  return (
                    <div key={provider} className="flex items-center gap-2 text-[11px] font-mono">
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white shrink-0"
                        style={{ background: meta.color }}
                        title={meta.label}
                      >
                        {(meta.letter || "?")}
                      </span>
                      <span className="truncate opacity-80" title={uid}>{uid}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cross-site presence */}
          {(user.sites_used || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] opacity-60 uppercase font-mono mb-2 flex items-center gap-1">
                <Globe2 className="w-3 h-3" /> Sites used
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(user.sites_used || []).map((s) => (
                  <span
                    key={s}
                    className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                    style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Login history table — most recent first */}
          {(user.login_history || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] opacity-60 uppercase font-mono mb-2">Recent sign-ins</div>
              <div className="max-h-56 overflow-y-auto -mx-2 px-2">
                <table className="w-full text-[11px] font-mono">
                  <thead>
                    <tr className="opacity-60 text-left">
                      <th className="py-1 pr-2">When</th>
                      <th className="py-1 pr-2">Provider</th>
                      <th className="py-1 pr-2">Site</th>
                      <th className="py-1">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(user.login_history || [])].reverse().slice(0, 30).map((h, i) => {
                      const meta = PROVIDER_META[h.provider] || { letter: "?", color: "#666" };
                      return (
                        <tr key={i} className="border-t" style={{ borderColor: "var(--wofy-border)" }}>
                          <td className="py-1 pr-2 opacity-80" title={h.at}>{relativeTime(h.at)}</td>
                          <td className="py-1 pr-2">
                            <span
                              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold text-white"
                              style={{ background: meta.color }}
                              title={h.provider}
                            >
                              {meta.letter}
                            </span>
                          </td>
                          <td className="py-1 pr-2 opacity-80">{h.site || "—"}</td>
                          <td className="py-1 opacity-60">{h.ip || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(user.login_history || []).length === 0 && (
            <p className="mt-3 text-xs opacity-60">No sign-ins recorded yet.</p>
          )}
        </div>

        {/* History */}
        <div className="mt-6">
          <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
            <History className="w-3 h-3" /> Audit history
          </div>
          <ol className="mt-3 space-y-3">
            {history.length === 0 && <li className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No history yet.</li>}
            {history.map((ev) => (
              <li key={ev.id} className="border-l-2 pl-3 py-1" style={{ borderColor: "var(--wofy-primary)" }}>
                <div className="text-xs font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                  {new Date(ev.created_at).toLocaleString()} · {ev.action}
                </div>
                {ev.meta?.from && <div className="text-xs">{ev.meta.from} → {ev.meta.to}</div>}
                {ev.comment && <div className="text-sm mt-1">"{ev.comment}"</div>}
                <div className="text-[11px] mt-1" style={{ color: "var(--wofy-text-soft)" }}>— {ev.admin_name}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
